import { useCallback, useEffect, useRef, useState } from 'react'
import { Plan } from '../types'
import { ApiError, DocumentSlot, RemoteDocument, api } from './api'
import { normalizePlan } from './storage'
import { readCache, writeCache, clearCache } from './localCache'
import { RemoteSave, onRemoteSave } from './live'

/**
 * Keeps one saved document in step with the server.
 *
 * The app edits state locally and this pushes the whole document back a moment
 * later. That is a coarse thing to do, and it is the right one here: the
 * projection engine is in the browser, every change re-runs it anyway, and a
 * year of a household's transactions is a few tens of kilobytes. Per-item
 * endpoints would buy nothing except a second data model to keep honest.
 *
 * What that choice does cost is a lost-update problem — two open tabs, or a
 * phone and a laptop — so every save carries the version it started from and
 * the server refuses to overwrite a newer one. That refusal surfaces as a
 * conflict the person resolves, rather than a save that silently loses work.
 */

/** Long enough to coalesce a burst of typing, short enough to survive a closed tab. */
const SAVE_DEBOUNCE_MS = 900
const RETRY_MIN_MS = 2_000
const RETRY_MAX_MS = 30_000

export type SyncStatus =
  | { kind: 'saved'; at: number | null }
  /** Edited, waiting for the debounce to elapse. */
  | { kind: 'pending' }
  | { kind: 'saving' }
  /** The server is unreachable. The change is held and retried. */
  | { kind: 'offline'; retryingAt: number }
  /** Someone else saved this document after we loaded it. */
  | { kind: 'conflict'; theirs: RemoteDocument | null }
  /** The server refused the document — a bug or a limit, not something a retry fixes. */
  | { kind: 'rejected'; message: string }

export type LoadPhase =
  | { phase: 'loading' }
  /** Signed in, but the account hasn't confirmed its email and the server requires it. */
  | { phase: 'needs-verification'; message: string }
  | { phase: 'unavailable'; message: string }
  | { phase: 'ready'; initial: Plan | null; fromCache: boolean }

export interface SyncedDocument {
  load: LoadPhase
  status: SyncStatus
  /** Record a change. Saves are debounced and coalesced. */
  push: (next: Plan) => void
  /** Send whatever is pending right now. */
  flushNow: () => void
  /** Re-read from the server, discarding anything unsaved. */
  reload: () => void
  /** Conflict resolution: overwrite the server with what is on screen. */
  keepMine: () => void
  /** Conflict resolution: take the server's copy. Returns the state to adopt. */
  takeTheirs: () => Plan | null
  /**
   * The newest copy saved from another tab or device, already agreed as this
   * tab's version — for the app to put on screen. Only set when listening
   * (`live`), and only when this tab had nothing unsaved of its own.
   */
  remote: { plan: Plan; at: number } | null
}

export function useSyncedDocument(slot: DocumentSlot, userId: string, { live = false } = {}): SyncedDocument {
  const [load, setLoad] = useState<LoadPhase>({ phase: 'loading' })
  const [status, setStatus] = useState<SyncStatus>({ kind: 'saved', at: null })
  const [remote, setRemote] = useState<SyncedDocument['remote']>(null)

  /** The version this browser's copy descends from; sent with every save. */
  const versionRef = useRef(0)
  /** Edited state not yet accepted by the server. */
  const pendingRef = useRef<Plan | null>(null)
  /** The exact object last agreed with the server, so re-renders don't cause saves. */
  const syncedRef = useRef<Plan | null>(null)
  const inFlightRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const backoffRef = useRef(RETRY_MIN_MS)
  /** Set by "keep mine": the next save ignores the version check. */
  const forceRef = useRef(false)
  /**
   * Mirrors the conflict status. `push` runs from event handlers that captured
   * an older render, so reading the state variable there would let edits go on
   * scheduling saves the server has already refused.
   */
  const conflictRef = useRef(false)
  const mountedRef = useRef(true)
  /** Numbers each load, so only the latest one's answer is used. */
  const loadSeqRef = useRef(0)

  const cacheKey = `${userId}:${slot}`

  const cancelTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const flush = useCallback(
    async (keepalive = false) => {
      cancelTimer()
      const next = pendingRef.current
      if (next === null || inFlightRef.current) return

      inFlightRef.current = true
      pendingRef.current = null
      if (!keepalive) setStatus({ kind: 'saving' })

      try {
        const saved = await api.putState(slot, next, forceRef.current ? null : versionRef.current, keepalive)
        forceRef.current = false
        versionRef.current = saved.version
        syncedRef.current = next
        backoffRef.current = RETRY_MIN_MS
        writeCache(cacheKey, next, saved.version)
        if (mountedRef.current && pendingRef.current === null) {
          setStatus({ kind: 'saved', at: Date.now() })
        }
      } catch (e) {
        // Hold on to the edit. A newer one may have arrived while this was in
        // flight, in which case that one wins and this one is already inside it.
        if (pendingRef.current === null) pendingRef.current = next

        if (!(e instanceof ApiError)) throw e

        if (e.code === 'conflict') {
          // Don't retry: the same save would be refused again. The person
          // picks a side, and their choice restarts the loop.
          conflictRef.current = true
          if (mountedRef.current) setStatus({ kind: 'conflict', theirs: e.current ?? null })
          return
        }

        if (e.isNetworkFailure) {
          const wait = backoffRef.current
          backoffRef.current = Math.min(wait * 2, RETRY_MAX_MS)
          if (mountedRef.current) setStatus({ kind: 'offline', retryingAt: Date.now() + wait })
          timerRef.current = setTimeout(() => void flush(), wait)
          return
        }

        // Held, not retried: the same save would be refused again. The Retry
        // button sends it once more, and so does the next edit.
        if (mountedRef.current) setStatus({ kind: 'rejected', message: e.message })
        return
      } finally {
        inFlightRef.current = false
      }

      // An edit that arrived mid-save still needs sending.
      if (pendingRef.current !== null && mountedRef.current) {
        timerRef.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS)
      }
    },
    [slot, cacheKey],
  )

  const push = useCallback(
    (next: Plan) => {
      // The reducer hands back a new object on every action, but adopting the
      // server's copy puts an object here that came *from* the server — saving
      // that straight back would be a pointless round trip.
      if (next === syncedRef.current) return

      pendingRef.current = next

      // While a conflict is unresolved, hold the edit rather than firing saves
      // the server is certain to refuse. It goes out as soon as it's resolved.
      if (conflictRef.current) return

      setStatus({ kind: 'pending' })
      cancelTimer()
      timerRef.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS)
    },
    [flush],
  )

  const doLoad = useCallback(async () => {
    const seq = ++loadSeqRef.current
    // Whether a newer load (or unmounting) has overtaken this one. A load that
    // lands late mustn't replace what the newer one opened on: each new load
    // makes the app reopen on its result, so a stale one arriving second would
    // throw away whatever the person had started doing.
    const superseded = () => seq !== loadSeqRef.current

    setLoad({ phase: 'loading' })
    conflictRef.current = false
    forceRef.current = false
    try {
      const doc = await api.getState(slot)
      if (superseded()) return
      versionRef.current = doc.version
      const data = doc.data ? normalizePlan(doc.data) : null
      syncedRef.current = data
      if (data) writeCache(cacheKey, data, doc.version)
      setLoad({ phase: 'ready', initial: data, fromCache: false })
      setStatus({ kind: 'saved', at: doc.updatedAt ? Date.parse(doc.updatedAt) : null })
    } catch (e) {
      if (superseded()) return
      if (!(e instanceof ApiError)) throw e

      if (e.code === 'forbidden') {
        setLoad({ phase: 'needs-verification', message: e.message })
        return
      }

      if (e.isNetworkFailure) {
        // Fall back to the last copy this browser saw, so a phone with no
        // signal still shows the plan instead of an error page. Edits made
        // from here queue and go out when the connection returns.
        const cached = readCache(cacheKey)
        if (cached) {
          versionRef.current = cached.version
          syncedRef.current = cached.data
          setLoad({ phase: 'ready', initial: cached.data, fromCache: true })
          setStatus({ kind: 'offline', retryingAt: Date.now() })
          return
        }
      }

      setLoad({ phase: 'unavailable', message: e.message })
    }
  }, [slot, cacheKey])

  useEffect(() => {
    mountedRef.current = true
    void doLoad()
    return () => {
      mountedRef.current = false
      // Anything still in flight belongs to a mount that's gone. (In
      // development, StrictMode mounts twice, and this is what keeps the
      // first mount's load from landing after the second's.) The rule below
      // expects a ref to a DOM node; this one is a counter, and bumping its
      // value at cleanup time is the point.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      loadSeqRef.current++
      cancelTimer()
    }
  }, [doLoad])

  /**
   * Another tab or device saved. If this tab is up to date and has nothing of
   * its own waiting, take theirs; otherwise leave it — this tab's own save will
   * meet the version check and the conflict bar, which is the right place to
   * choose between two sets of edits.
   */
  const pullIfNewer = useCallback(
    async (version: number) => {
      const busy = () => pendingRef.current !== null || inFlightRef.current || conflictRef.current
      // Our own save comes back as an event too; it's no newer than what we have.
      if (version <= versionRef.current || busy()) return
      let doc: RemoteDocument
      try {
        doc = await api.getState(slot)
      } catch {
        return // Unreachable now; the next save's version check still holds.
      }
      if (!mountedRef.current || doc.version <= versionRef.current || busy() || !doc.data) return
      const plan = normalizePlan(doc.data)
      versionRef.current = doc.version
      syncedRef.current = plan
      writeCache(cacheKey, plan, doc.version)
      setStatus({ kind: 'saved', at: doc.updatedAt ? Date.parse(doc.updatedAt) : Date.now() })
      setRemote({ plan, at: Date.now() })
    },
    [slot, cacheKey],
  )

  useEffect(() => {
    if (!live) return
    return onRemoteSave(userId, (save: RemoteSave) => {
      if (save.slot === slot || save.slot === '*') void pullIfNewer(save.version)
    })
  }, [live, userId, slot, pullIfNewer])

  // A phone doesn't get an unload event when the app is swiped away, but it
  // does get this. keepalive lets the request outlive the page.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden' && pendingRef.current !== null) void flush(true)
    }
    const onOnline = () => {
      if (pendingRef.current !== null) {
        backoffRef.current = RETRY_MIN_MS
        void flush()
      }
    }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', onHide)
    window.addEventListener('online', onOnline)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', onHide)
      window.removeEventListener('online', onOnline)
    }
  }, [flush])

  return {
    load,
    status,
    remote,
    push,
    flushNow: () => void flush(),
    reload: () => {
      pendingRef.current = null
      cancelTimer()
      void doLoad()
    },
    keepMine: () => {
      forceRef.current = true
      conflictRef.current = false
      backoffRef.current = RETRY_MIN_MS
      void flush()
    },
    takeTheirs: () => {
      if (status.kind !== 'conflict' || !status.theirs) return null
      const adopted = status.theirs.data ? normalizePlan(status.theirs.data) : null
      versionRef.current = status.theirs.version
      syncedRef.current = adopted
      pendingRef.current = null
      forceRef.current = false
      conflictRef.current = false
      if (adopted) writeCache(cacheKey, adopted, status.theirs.version)
      else clearCache(cacheKey)
      setStatus({ kind: 'saved', at: Date.now() })
      return adopted
    },
  }
}
